import { onBeforeUnmount, onMounted, ref } from 'vue';
import EvolutionAPI from 'dashboard/api/evolution';

const POLL_INTERVAL_MS = 4000;
// 'error' (e.g. bad GLOBAL_API_KEY) won't self-heal by polling — the admin has to fix the
// credential and hit Reconnect, which calls startPolling() again.
const TERMINAL_STATUSES = ['open', 'error'];

// Polls the Evolution connection status (and, while pairing, the QR code) for an
// inbox until it reaches a terminal state — pauses while the browser tab is hidden
// and resumes on visibility, modeled on the poll-while-active-and-visible pattern
// used by settings/data/Show.vue.
export function useEvolutionStatusPoll(inboxId) {
  const status = ref('connecting');
  const phoneNumber = ref(null);
  const qrCode = ref(null);
  const disconnectReason = ref(null);
  const lastError = ref(null);
  const isFetchingQr = ref(false);
  const isPolling = ref(false);

  let timer = null;

  const fetchStatus = async () => {
    try {
      const { data } = await EvolutionAPI.status(inboxId);
      status.value = data.connection_status;
      phoneNumber.value = data.phone_number;
      disconnectReason.value = data.disconnect_reason || null;
      lastError.value = data.last_error || null;
    } catch (error) {
      // keep last known status on a transient failure, next tick will retry
    }
  };

  const fetchQr = async () => {
    isFetchingQr.value = true;
    try {
      const { data } = await EvolutionAPI.qr(inboxId);
      qrCode.value = data.qrcode || null;
    } catch (error) {
      qrCode.value = null;
    } finally {
      isFetchingQr.value = false;
    }
  };

  const stopPolling = () => {
    if (!timer) return;
    window.clearInterval(timer);
    timer = null;
    isPolling.value = false;
  };

  const tick = async () => {
    if (document.hidden) return;

    await fetchStatus();
    if (status.value === 'qr_pending') {
      await fetchQr();
    } else {
      qrCode.value = null;
    }

    if (TERMINAL_STATUSES.includes(status.value)) {
      stopPolling();
    }
  };

  const startPolling = () => {
    if (timer || TERMINAL_STATUSES.includes(status.value)) return;
    isPolling.value = true;
    tick();
    timer = window.setInterval(tick, POLL_INTERVAL_MS);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      stopPolling();
    } else {
      startPolling();
    }
  };

  onMounted(() => {
    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });

  onBeforeUnmount(() => {
    stopPolling();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });

  return {
    status,
    phoneNumber,
    qrCode,
    disconnectReason,
    lastError,
    isFetchingQr,
    isPolling,
    startPolling,
    stopPolling,
    fetchStatus,
    fetchQr,
  };
}
